import React from 'react';
import { AlertCircle, Mail, Phone } from 'lucide-react';
import SEO from '../components/SEO';

const ComplaintBoardPage = () => {
  const currentDate = new Date();
  const monthYear = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Complaint Board | Investor Grievance Status | Equi Pulse"
        description="View complaint resolution status and grievance history at Equi Pulse Research. Transparent handling of investor complaints as per SEBI guidelines."
        url="/complaint-board"
      />
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1920')] bg-cover bg-center opacity-10" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block mb-4 px-4 py-1.5 bg-amber-900/30 border border-amber-500/30 rounded-full">
              <p className="text-amber-400 text-sm font-semibold">SEBI Registration No.: INH000024046</p>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Complaint <span className="text-amber-400">Board</span>
            </h1>
            <p className="text-lg text-gray-300">
              Complaints for the Month ending: {monthYear}
            </p>
          </div>
        </div>
      </section>

      {/* Complaint Board Table */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Information Notice */}
            <div className="mb-8 p-6 bg-emerald-900/20 border border-emerald-500/30 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-emerald-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <div>
                  <h3 className="text-lg font-semibold text-emerald-400 mb-2">Transparent Complaint Tracking</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    As per SEBI guidelines, we maintain complete transparency regarding client complaints. This board is updated monthly to reflect our commitment to client satisfaction and regulatory compliance.
                  </p>
                </div>
              </div>
            </div>

            {/* Complaint Statistics Table */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-amber-500/20 overflow-hidden shadow-2xl">
              <div className="overflow-x-auto">
                <table className="w-full" aria-label="Monthly complaint statistics">
                  <thead>
                    <tr className="bg-gradient-to-r from-emerald-900/40 to-amber-900/40 border-b border-amber-500/20">
                      <th scope="col" className="px-6 py-4 text-left text-sm font-semibold text-amber-400">Sr. No.</th>
                      <th scope="col" className="px-6 py-4 text-left text-sm font-semibold text-amber-400">Received From</th>
                      <th scope="col" className="px-6 py-4 text-center text-sm font-semibold text-amber-400">Pending at the end of last month</th>
                      <th scope="col" className="px-6 py-4 text-center text-sm font-semibold text-amber-400">Received during the month</th>
                      <th scope="col" className="px-6 py-4 text-center text-sm font-semibold text-amber-400">Resolved during the month</th>
                      <th scope="col" className="px-6 py-4 text-center text-sm font-semibold text-amber-400">Total Pending complaints</th>
                      <th scope="col" className="px-6 py-4 text-center text-sm font-semibold text-amber-400">Average Resolution Time (in days)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-700/50 hover:bg-slate-800/50 transition-colors duration-200">
                      <td className="px-6 py-4 text-gray-300">1</td>
                      <td className="px-6 py-4 text-gray-300">Directly from Investors</td>
                      <td className="px-6 py-4 text-center text-gray-300">0</td>
                      <td className="px-6 py-4 text-center text-gray-300">0</td>
                      <td className="px-6 py-4 text-center text-emerald-400 font-semibold">0</td>
                      <td className="px-6 py-4 text-center text-gray-300">0</td>
                      <td className="px-6 py-4 text-center text-gray-300">0</td>
                    </tr>
                    <tr className="border-b border-slate-700/50 hover:bg-slate-800/50 transition-colors duration-200">
                      <td className="px-6 py-4 text-gray-300">2</td>
                      <td className="px-6 py-4 text-gray-300">SEBI (SCORES)</td>
                      <td className="px-6 py-4 text-center text-gray-300">0</td>
                      <td className="px-6 py-4 text-center text-gray-300">0</td>
                      <td className="px-6 py-4 text-center text-emerald-400 font-semibold">0</td>
                      <td className="px-6 py-4 text-center text-gray-300">0</td>
                      <td className="px-6 py-4 text-center text-gray-300">0</td>
                    </tr>
                    <tr className="border-b border-slate-700/50 hover:bg-slate-800/50 transition-colors duration-200">
                      <td className="px-6 py-4 text-gray-300">3</td>
                      <td className="px-6 py-4 text-gray-300">Other Sources (if any)</td>
                      <td className="px-6 py-4 text-center text-gray-300">0</td>
                      <td className="px-6 py-4 text-center text-gray-300">0</td>
                      <td className="px-6 py-4 text-center text-emerald-400 font-semibold">0</td>
                      <td className="px-6 py-4 text-center text-gray-300">0</td>
                      <td className="px-6 py-4 text-center text-gray-300">0</td>
                    </tr>
                    <tr className="bg-amber-900/20 font-semibold">
                      <td className="px-6 py-4 text-amber-400" colSpan="2">Grand Total</td>
                      <td className="px-6 py-4 text-center text-white">0</td>
                      <td className="px-6 py-4 text-center text-white">0</td>
                      <td className="px-6 py-4 text-center text-emerald-400">0</td>
                      <td className="px-6 py-4 text-center text-white">0</td>
                      <td className="px-6 py-4 text-center text-white">0</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Note Section */}
            <div className="mt-8 p-6 bg-slate-900/50 border border-slate-700/50 rounded-xl">
              <p className="text-gray-400 text-sm text-center">
                <strong className="text-emerald-400">Note:</strong> This complaint board is updated on the first working day of every month and reflects complaints received and resolved during the previous month.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Grievance Redressal Section */}
      <section className="py-20 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-white mb-12">
              How to <span className="text-amber-400">Lodge a Complaint</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="p-6 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl border border-emerald-500/20">
                <div className="flex items-center justify-center w-12 h-12 bg-emerald-900/30 rounded-lg mb-4">
                  <span className="text-2xl font-bold text-emerald-400">1</span>
                </div>
                <h3 className="text-lg font-semibold text-emerald-400 mb-2">Contact Us First</h3>
                <p className="text-gray-300 text-sm">
                  Reach out to our compliance team with your concern. Most issues are resolved at this stage.
                </p>
              </div>

              <div className="p-6 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl border border-amber-500/20">
                <div className="flex items-center justify-center w-12 h-12 bg-amber-900/30 rounded-lg mb-4">
                  <span className="text-2xl font-bold text-amber-400">2</span>
                </div>
                <h3 className="text-lg font-semibold text-amber-400 mb-2">SEBI SCORES</h3>
                <p className="text-gray-300 text-sm">
                  If unresolved, lodge your complaint on SEBI SCORES portal for official intervention.
                </p>
              </div>

              <div className="p-6 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl border border-emerald-500/20">
                <div className="flex items-center justify-center w-12 h-12 bg-emerald-900/30 rounded-lg mb-4">
                  <span className="text-2xl font-bold text-emerald-400">3</span>
                </div>
                <h3 className="text-lg font-semibold text-emerald-400 mb-2">ODR Platform</h3>
                <p className="text-gray-300 text-sm">
                  Use the Online Dispute Resolution mechanism for faster resolution.
                </p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-slate-800/50 p-8 rounded-xl border border-amber-500/20">
              <h3 className="text-xl font-bold text-amber-400 mb-6 text-center">Contact for Complaints</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-emerald-900/30 rounded-lg">
                    <Mail className="w-6 h-6 text-emerald-400" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Email</p>
                    <a href="mailto:compliance@equipulseresearch.com" className="text-amber-400 hover:text-amber-300 font-medium">
                      compliance@equipulseresearch.com
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-3 bg-amber-900/30 rounded-lg">
                    <Phone className="w-6 h-6 text-amber-400" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Phone</p>
                    <a href="tel:+918982892301" className="text-amber-400 hover:text-amber-300 font-medium">
                      +91-8982892301
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-700">
                <p className="text-gray-400 text-sm text-center">
                  <strong>Response Time:</strong> All complaints are acknowledged within 5 business days and resolved within 15 business days wherever possible.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ComplaintBoardPage;